import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to track if user has scrolled to the bottom of the page
 * Returns true if scrolled to bottom OR if no scrolling is needed
 */
export function useScrollToActivate(threshold: number = 50) {
  const [canActivate, setCanActivate] = useState(false);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    
    // Check if no scrolling is needed (content fits in viewport)
    const noScrollNeeded = scrollHeight <= clientHeight + 10;
    
    // Check if scrolled to bottom (with threshold for tolerance)
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    setCanActivate(noScrollNeeded || scrolledToBottom);
  }, [threshold]);

  useEffect(() => {
    // Initial check after a short delay to allow DOM to settle
    checkTimeoutRef.current = setTimeout(checkScroll, 100);

    // Check on scroll
    window.addEventListener('scroll', checkScroll, { passive: true });
    
    // Check on resize (content might fit after resize)
    window.addEventListener('resize', checkScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [checkScroll]);

  // Re-check when content might have changed
  const recheckScroll = useCallback(() => {
    setTimeout(checkScroll, 100);
  }, [checkScroll]);

  return { canActivate, recheckScroll };
}
